import React, { useMemo } from 'react';
import { analyticsSnapshot } from '../../data/analyticsSnapshot';
import MetricSummary from '../common/MetricSummary';
import { sumArray, findLatestValue } from '../../utils/dataHelpers';
import ChartCard from '../common/ChartCard';
import { ChartData } from 'chart.js';

const AdsOverviewPage: React.FC = () => {
  const platforms = Array.from(analyticsSnapshot.adsAnalytics.platforms);
  const aiPlatforms = ((analyticsSnapshot as any).aiAnalysis?.adsAnalytics?.platforms ?? []) as ReadonlyArray<any>;

  const totalImpressions = platforms.reduce<number>(
    (acc, platform) => acc + sumArray(platform.metricsSummary.datasets.data),
    0
  );
  const totalCampaigns = platforms.reduce<number>((acc, platform) => acc + platform.campaigns.length, 0);
  const topPlatform = platforms.reduce<{ name: string; impressions: number }>(
    (acc, platform) => {
      const impressions = sumArray(platform.metricsSummary.datasets.data);
      return impressions > acc.impressions ? { name: platform.platform, impressions } : acc;
    },
    { name: platforms[0]?.platform ?? '-', impressions: 0 }
  );

  const impressionsChartData = useMemo<ChartData<'line'>>(
    () => ({
      labels: Array.from(analyticsSnapshot.labels),
      datasets: platforms.map((platform) => ({
        label: platform.platform,
        data: Array.from(platform.metricsSummary.datasets.data)
      }))
    }),
    [platforms]
  );

  return (
    <div className="space-y-6 md:space-y-10">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-white">Reklam Analitiği Özeti</h1>
        <p className="text-white/70 mt-2 text-sm md:text-base">
          Platform bazlı gösterim, kampanya dönüşümleri ve son dönem performans öne çıkanları.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MetricSummary
          title="Toplam Gösterim"
          value={totalImpressions}
          description="Tüm platformlarda yıl içindeki toplam gösterim."
        />
        <MetricSummary
          title="Aktif Kampanya"
          value={totalCampaigns}
          description="Şu anda izlenen toplam kampanya sayısı."
        />
        <MetricSummary
          title="En Güçlü Platform"
          value={topPlatform.impressions}
          description={`Ön plana çıkan platform: ${topPlatform.name}`}
        />
      </div>

      <ChartCard
        title="Platform Bazlı Gösterim Trendleri"
        description="Tüm reklam platformlarının aylık gösterim performansını karşılaştırın."
        data={impressionsChartData}
        chartType="line"
      />

      {platforms.map((platform) => {
        const platformAI = aiPlatforms.find((item: any) => item.platform === platform.platform) as any;
        const metricsInsights = (platformAI?.metricsSummary?.insights ?? []) as ReadonlyArray<any>;
        const campaignInsightsMap = new Map<string, ReadonlyArray<any>>();
        (platformAI?.campaigns as ReadonlyArray<any> | undefined)?.forEach((campaign: any) => {
          campaignInsightsMap.set(campaign.campaignId, campaign.insights ?? []);
        });

        const platformChartData: ChartData<'line'> = {
          labels: Array.from(platform.metricsSummary.labels),
          datasets: [
            {
              label: 'Gösterim',
              data: Array.from(platform.metricsSummary.datasets.data)
            }
          ]
        };

        return (
          <section key={platform.platform} className="space-y-6">
            <div>
              <h2 className="text-2xl font-semibold text-white">{platform.platform}</h2>
              <p className="text-white/60 text-sm">
                Hesap: {platform.accountId === 'unknown' ? 'Belirtilmedi' : platform.accountId}
              </p>
            </div>

            <ChartCard
              title="Gösterim Trendleri"
              description="Platform genelinde aylık gösterim performansı."
              data={platformChartData}
              chartType="line"
              insights={metricsInsights}
            />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {platform.campaigns.map((campaign) => {
                const latest = findLatestValue(campaign.metrics.datasets.data);
                const chartData: ChartData<'bar'> = {
                  labels: Array.from(campaign.metrics.labels),
                  datasets: [
                    {
                      label: campaign.metrics.datasets.label,
                      data: Array.from(campaign.metrics.datasets.data)
                    }
                  ]
                };

                return (
                  <ChartCard
                    key={campaign.campaignId}
                    title={campaign.campaignName}
                    description={`Durum: ${campaign.status} • Son veri ${
                      latest.index >= 0 ? campaign.metrics.labels[latest.index] : 'Veri yok'
                    }`}
                    chartType="bar"
                    data={chartData}
                    insights={campaignInsightsMap.get(campaign.campaignId) ?? []}
                  />
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default AdsOverviewPage;

